import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import AudioPlayer from "@/components/AudioPlayer";
import BookPreview from "@/components/BookPreview";
import ProductReviews from "@/components/ProductReviews";
import StreamPreview from "@/components/StreamPreview";
import { api as apiClient } from "@/lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const [p, setP] = useState(null);
  const [streamMeta, setStreamMeta] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setP(data); setActiveIdx(0);
      if (data.category === "ticket" && data.event_dates && data.event_dates.length === 1) {
        setSelectedDate(data.event_dates[0]);
      }
      if (data.category === "apparel") {
        if (data.sizes && data.sizes.length === 1) setSelectedSize(data.sizes[0]);
        if (data.colors && data.colors.length === 1) setSelectedColor(data.colors[0]);
      }
      if (data.category === "stream") {
        apiClient.get(`/streams/${id}`).then(({ data: sm }) => setStreamMeta(sm)).catch(() => {});
      }
    }).catch(() => setP(null));
  }, [id]);

  if (!p) return <Layout><div className="max-w-[1400px] mx-auto px-6 py-24 mono uppercase text-sm tracking-[0.2em]">Loading…</div></Layout>;

  const gallery = (p.images && p.images.length ? p.images : [p.image_url]).filter(Boolean);
  const mainImg = (selectedColor && selectedColor.image_url) || gallery[activeIdx] || p.image_url;

  return (
    <Layout>
      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <Link to="/" className="mono text-xs uppercase tracking-[0.25em] text-[#555] hover:text-[#C77E3C]">← Back to store</Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6">
          <div className="lg:col-span-7">
            <div className="brut-border bg-white overflow-hidden">
              {mainImg ? (
                <img data-testid="product-main-image" src={mainImg} alt={p.title} className="w-full aspect-[4/5] object-cover" />
              ) : (
                <div className="aspect-[4/5] bg-[#EAEAEA] grid place-items-center mono text-sm uppercase">No image</div>
              )}
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2" data-testid="product-thumbs">
                {gallery.map((url, i) => (
                  <button key={i} onClick={() => setActiveIdx(i)} data-testid={`thumb-${i}`}
                    className={`aspect-square brut-border overflow-hidden ${activeIdx === i ? "brut-shadow-accent" : ""}`}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {p.preview_url && p.category === "music" && (
              <div className="mt-4">
                <AudioPlayer
                  src={p.preview_url.startsWith("http") ? p.preview_url : `${process.env.REACT_APP_BACKEND_URL}${p.preview_url}`}
                  title="Audio preview"
                />
              </div>
            )}
            {p.preview_url && p.category === "book" && (
              <div className="mt-4">
                <BookPreview
                  src={p.preview_url.startsWith("http") ? p.preview_url : `${process.env.REACT_APP_BACKEND_URL}${p.preview_url}`}
                  title="Sample pages"
                />
              </div>
            )}
            {p.category === "stream" && streamMeta && (
              <StreamPreview product={streamMeta} />
            )}
          </div>
          <div className="lg:col-span-5">
            <span className="pill-label">{p.type} · {p.category}</span>
            <h1 className="heading text-4xl md:text-5xl font-black uppercase tracking-tight mt-3">{p.title}</h1>
            <div className="mono text-xs text-[#555] mt-2 uppercase tracking-[0.2em]">by {p.seller_name}</div>
            <div className="mt-6 inline-block brut-border bg-[#C77E3C] text-white px-4 py-2 mono font-bold text-2xl" data-testid="product-price">
              {p.price === 0 ? "FREE" : `$${p.price.toFixed(2)}`}
            </div>
            <p className="mt-6 text-[#333] leading-relaxed whitespace-pre-line">{p.description}</p>
            <ProductReviews productId={p.product_id} />
            {p.category === "stream" && (
              <div className="mt-4 brut-border bg-[#FDF3E9] p-3 mono text-[10px] uppercase tracking-[0.3em] text-[#1F3D2A]">
                🎧 Buy once — stream for life. Free 3-min preview. No downloads.
              </div>
            )}
            {p.category === "stream" && streamMeta?.entitled && (
              <Link
                to={`/stream/${p.product_id}`}
                data-testid="streamnow-btn"
                className="mt-4 block brut-border bg-[#C77E3C] text-white text-center px-4 py-4 heading uppercase tracking-tight font-black text-2xl hover:bg-[#1F3D2A]"
              >
                #STREAMNOW →
              </Link>
            )}
            {p.type === "physical" && p.delivery_days && (
              <div className="mt-4 pill-label">📦 Delivers in {p.delivery_days} days</div>
            )}
            {p.type === "physical" && (
              <div className="mt-3 mono text-xs uppercase tracking-[0.2em] text-[#555]" data-testid="product-shipping-info">
                {p.free_shipping ? (
                  <span>🚚 Free shipping</span>
                ) : (
                  <>
                    <span>🚚 Shipping: ${(p.shipping_price || 0).toFixed(2)}{p.shipping_vat_included ? " (incl. VAT)" : ""}</span>
                    {p.allow_pickup && p.pickup_city && (
                      <span className="ml-3">or local pickup ({p.pickup_city}, {p.pickup_country})</span>
                    )}
                  </>
                )}
              </div>
            )}
            {p.category === "apparel" && (() => {
              const vs = p.variant_stock || {};
              const stockOf = (sz, cn) => {
                if (!Object.keys(vs).length) return null;
                return parseInt(vs[`${sz || ""}|${cn || ""}`] || 0);
              };
              const currentStock = stockOf(selectedSize, selectedColor?.name);
              return (
                <div className="mt-6 brut-border bg-[#FDF3E9] p-4 space-y-4">
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">Apparel · {p.apparel_section}</div>
                  {p.sizes && p.sizes.length > 0 && (
                    <div>
                      <div className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Size {p.sizes.length > 1 ? "*" : ""}</div>
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="size-picker">
                        {p.sizes.map((s) => {
                          const hasStock = !Object.keys(vs).length || (p.colors || []).some((c) => stockOf(s, c.name) > 0) || (!p.colors?.length && stockOf(s, "") > 0);
                          return (
                            <button key={s} type="button" disabled={!hasStock} data-testid={`size-${s}`} onClick={() => setSelectedSize(s)}
                              className={`brut-border px-3 py-2 mono text-xs uppercase tracking-[0.2em] min-w-[48px] ${!hasStock ? "opacity-40 line-through cursor-not-allowed" : ""} ${selectedSize === s ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {p.colors && p.colors.length > 0 && (
                    <div>
                      <div className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Colour {p.colors.length > 1 ? "*" : ""}</div>
                      <div className="mt-2 flex flex-wrap gap-2" data-testid="color-picker">
                        {p.colors.map((c, i) => {
                          const stk = selectedSize ? stockOf(selectedSize, c.name) : ((p.sizes || []).some((s) => stockOf(s, c.name) > 0) ? 1 : (Object.keys(vs).length ? 0 : null));
                          const outOfStock = stk === 0;
                          return (
                            <button key={i} type="button" disabled={outOfStock} data-testid={`color-${c.name}`} onClick={() => setSelectedColor(c)}
                              title={c.name}
                              className={`brut-border p-1 ${outOfStock ? "opacity-40 cursor-not-allowed" : ""} ${selectedColor?.name === c.name ? "brut-shadow-accent" : ""}`}>
                              <div className="w-12 h-12 overflow-hidden bg-[#EAEAEA]">
                                {c.image_url && <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />}
                              </div>
                              <div className="mono text-[9px] uppercase tracking-[0.15em] text-center mt-1">{c.name}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {selectedSize && (p.colors?.length ? selectedColor : true) && currentStock != null && (
                    <div data-testid="variant-stock-info" className={`mono text-[10px] uppercase tracking-[0.2em] ${currentStock <= 0 ? "text-[#C77E3C] font-bold" : currentStock <= 5 ? "text-[#C77E3C]" : "text-[#555]"}`}>
                      {currentStock <= 0 ? "This size/colour is sold out" : currentStock <= 5 ? `Only ${currentStock} left in ${selectedSize}${selectedColor ? ` · ${selectedColor.name}` : ""}` : `${currentStock} in stock`}
                    </div>
                  )}
                </div>
              );
            })()}
            {p.category === "ticket" && (
              <div className="mt-6 brut-border bg-[#FDF3E9] p-4 space-y-3">
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">Event</div>
                  <div className="text-sm mt-1"><strong>{p.event_location || "TBA"}</strong></div>
                  <div className="mono text-xs text-[#555] mt-1">Contact: {p.event_contact || "—"}</div>
                </div>
                {p.event_dates && p.event_dates.length > 0 && (
                  <div>
                    <div className="mono text-[10px] uppercase tracking-[0.2em] font-bold">Pick a date *</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {p.event_dates.map((d) => (
                        <button key={d} data-testid={`date-opt-${d}`} type="button" onClick={() => setSelectedDate(d)}
                          className={`brut-border px-3 py-2 mono text-xs uppercase tracking-[0.2em] ${selectedDate === d ? "bg-[#1F3D2A] text-white" : "bg-white"}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {p.sold_out ? (
                  <div data-testid="ticket-sold-out" className="brut-border bg-[#1F3D2A] text-white px-3 py-2 mono text-xs uppercase tracking-[0.25em] text-center">Sold out</div>
                ) : p.tickets_remaining != null && p.tickets_remaining <= 5 ? (
                  <div data-testid="ticket-low-stock" className="brut-border bg-[#C77E3C] text-white px-3 py-2 mono text-xs uppercase tracking-[0.25em] text-center animate-pulse">
                    Hurry — only {p.tickets_remaining} ticket{p.tickets_remaining === 1 ? "" : "s"} left!
                  </div>
                ) : p.tickets_remaining != null ? (
                  <div className="mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{p.tickets_remaining} of {p.stock} tickets available</div>
                ) : null}
              </div>
            )}
            {p.stock !== null && p.stock !== undefined && p.category !== "ticket" && p.category !== "apparel" && (
              <div className="mono text-xs uppercase tracking-[0.2em] text-[#555] mt-4">{p.stock} in stock{p.sold_out && " · SOLD OUT"}</div>
            )}
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center brut-border bg-white">
                <button data-testid="qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 hover:bg-[#1F3D2A] hover:text-white">−</button>
                <span className="px-4 mono font-bold" data-testid="qty-value">{qty}</span>
                <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="px-4 py-3 hover:bg-[#1F3D2A] hover:text-white">+</button>
              </div>
              <button
                data-testid="add-to-cart-btn"
                disabled={p.sold_out}
                onClick={() => {
                  if (p.sold_out) { toast.error("Sold out"); return; }
                  if (p.category === "ticket" && p.event_dates && p.event_dates.length > 1 && !selectedDate) {
                    toast.error("Pick a date first"); return;
                  }
                  if (p.category === "apparel" && p.sizes && p.sizes.length > 1 && !selectedSize) {
                    toast.error("Pick a size first"); return;
                  }
                  if (p.category === "apparel" && p.colors && p.colors.length > 1 && !selectedColor) {
                    toast.error("Pick a colour first"); return;
                  }
                  if (p.category === "apparel" && p.variant_stock) {
                    const sz = selectedSize || (p.sizes?.length === 1 ? p.sizes[0] : "");
                    const cn = selectedColor?.name || (p.colors?.length === 1 ? p.colors[0].name : "");
                    const left = parseInt(p.variant_stock[`${sz}|${cn}`] || 0);
                    if (left <= 0) { toast.error("This variant is sold out"); return; }
                    if (qty > left) { toast.error(`Only ${left} left in ${sz}${cn ? " · "+cn : ""}`); return; }
                  }
                  if (p.category === "ticket" && p.tickets_remaining != null && qty > p.tickets_remaining) {
                    toast.error(`Only ${p.tickets_remaining} left`); return;
                  }
                  add(p, qty, {
                    selected_date: selectedDate || (p.event_dates && p.event_dates[0]) || null,
                    selected_size: selectedSize || (p.sizes && p.sizes.length === 1 ? p.sizes[0] : null),
                    selected_color: selectedColor?.name || (p.colors && p.colors.length === 1 ? p.colors[0].name : null),
                    color_image: selectedColor?.image_url || null,
                  });
                  toast.success("Added to cart");
                }}
                className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >{p.sold_out ? "Sold out" : "Add to cart"}</button>
            </div>
            <button
              data-testid="buy-now-btn"
              disabled={p.sold_out}
              onClick={() => {
                if (p.sold_out) { toast.error("Sold out"); return; }
                if (p.category === "ticket" && p.event_dates && p.event_dates.length > 1 && !selectedDate) {
                  toast.error("Pick a date first"); return;
                }
                if (p.category === "apparel" && p.sizes && p.sizes.length > 1 && !selectedSize) {
                  toast.error("Pick a size first"); return;
                }
                if (p.category === "apparel" && p.colors && p.colors.length > 1 && !selectedColor) {
                  toast.error("Pick a colour first"); return;
                }
                if (p.category === "apparel" && p.variant_stock) {
                  const sz = selectedSize || (p.sizes?.length === 1 ? p.sizes[0] : "");
                  const cn = selectedColor?.name || (p.colors?.length === 1 ? p.colors[0].name : "");
                  const left = parseInt(p.variant_stock[`${sz}|${cn}`] || 0);
                  if (left <= 0) { toast.error("This variant is sold out"); return; }
                  if (qty > left) { toast.error(`Only ${left} left in ${sz}${cn ? " · "+cn : ""}`); return; }
                }
                if (p.category === "ticket" && p.tickets_remaining != null && qty > p.tickets_remaining) {
                  toast.error(`Only ${p.tickets_remaining} left`); return;
                }
                add(p, qty, {
                  selected_date: selectedDate || (p.event_dates && p.event_dates[0]) || null,
                  selected_size: selectedSize || (p.sizes && p.sizes.length === 1 ? p.sizes[0] : null),
                  selected_color: selectedColor?.name || (p.colors && p.colors.length === 1 ? p.colors[0].name : null),
                  color_image: selectedColor?.image_url || null,
                });
                nav("/cart");
              }}
              className="btn-ghost w-full mt-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >Buy now →</button>
            <div className="mt-8 brut-border bg-[#1F3D2A] text-white p-5">
              <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#C77E3C]">Curated</div>
              <div className="text-sm mt-1 leading-relaxed">Every product on Jubasmash is reviewed by our team before going live. You&apos;re buying directly from an independent maker.</div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
